import { MessagesSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useThreads } from '../queries';

const PREVIEW_ROWS = 4;

/**
 * Dashboard card: what is waiting in chat.
 *
 * The count is summed from the thread list's server-computed `unread_count`
 * per thread — there is no aggregate endpoint, and re-deriving it from message
 * timestamps would be a second implementation of the read cursor.
 */
export function UnreadChatsCard() {
  const { t } = useTranslation(['chat']);
  const threads = useThreads();

  const rows = (threads.data?.results ?? []).filter(thread => thread.unread_count > 0);
  const total = rows.reduce((sum, thread) => sum + thread.unread_count, 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>{t('chat:unreadCard.title')}</CardTitle>
          <Button variant="outline" size="sm" asChild>
            <Link to="/chats">
              <MessagesSquare />
              {t('chat:unreadCard.open')}
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {threads.isLoading ? <Skeleton className="h-12 w-full" /> : null}
        {!threads.isLoading && total === 0 ? (
          <p className="text-muted-foreground text-sm">{t('chat:unreadCard.empty')}</p>
        ) : null}
        {total > 0 ? (
          <>
            <p className="text-sm font-medium">{t('chat:unreadCard.total', { count: total })}</p>
            <ul className="space-y-1">
              {rows.slice(0, PREVIEW_ROWS).map(thread => (
                <li key={thread.public_id} className="text-sm">
                  <Link className="text-primary underline" to={`/chats?thread=${thread.public_id}`}>
                    {thread.title || t(`chat:sections.${thread.kind}`)}
                  </Link>{' '}
                  <span className="text-muted-foreground text-xs">
                    {t('chat:unread', { count: thread.unread_count })}
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
